import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorderOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';

import productsApi from '../../../api/productsApi';
import { getErrorMessage } from '../../../utils/errorHandler';

// Galeria de imagenes de un producto ya existente: subir, marcar como destacada (la que se
// ve en el catalogo/listado) y eliminar. Cada accion es inmediata (no forma parte del envio
// del formulario de datos del producto), por eso avisa via `onChanged` para refrescar.
export default function ProductImagesManager({ productId, images, onChanged }) {
  const { enqueueSnackbar } = useSnackbar();
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    setUploading(true);
    try {
      await productsApi.uploadProductImage(productId, file);
      onChanged();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo subir la imagen.'), { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleSetPrimary = async (imageId) => {
    setBusyId(imageId);
    try {
      await productsApi.setPrimaryProductImage(productId, imageId);
      onChanged();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo marcar la imagen como destacada.'), { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (imageId) => {
    setBusyId(imageId);
    try {
      await productsApi.deleteProductImage(productId, imageId);
      onChanged();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar la imagen.'), { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Stack spacing={1}>
      <Stack sx={{ flexWrap: 'wrap', gap: 1.5 }} direction="row">
        {images.map((image) => (
          <Box
            key={image.id}
            sx={{
              position: 'relative',
              width: 96,
              height: 96,
              border: '1px solid',
              borderColor: image.is_primary ? 'primary.main' : 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={image.url}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <Stack
              direction="row"
              sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'background.paper', borderRadius: 1 }}
            >
              <Tooltip title={image.is_primary ? 'Imagen destacada' : 'Marcar como destacada'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleSetPrimary(image.id)}
                    disabled={image.is_primary || busyId === image.id}
                  >
                    {image.is_primary ? (
                      <StarIcon fontSize="small" color="warning" />
                    ) : (
                      <StarBorderIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Eliminar">
                <span>
                  <IconButton size="small" onClick={() => handleDelete(image.id)} disabled={busyId === image.id}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        ))}

        <Box
          component="label"
          sx={{
            width: 96,
            height: 96,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            cursor: uploading ? 'default' : 'pointer',
            color: 'text.secondary',
          }}
        >
          <AddPhotoAlternateOutlinedIcon />
          <Typography variant="caption">{uploading ? 'Subiendo...' : 'Subir'}</Typography>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            disabled={uploading}
            onChange={handleFileChange}
          />
        </Box>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        La primera imagen que subas queda como destacada; toca la estrella para cambiarla.
      </Typography>
    </Stack>
  );
}

ProductImagesManager.propTypes = {
  productId: PropTypes.number.isRequired,
  images: PropTypes.array.isRequired,
  onChanged: PropTypes.func.isRequired,
};
